import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "order-admin-zone min-w-0 w-full" }
const _hoisted_2 = { class: "order-admin-zone__panel border border-yellow-300 rounded overflow-hidden" }
const _hoisted_3 = { class: "w-full text-sm bg-white" }
const _hoisted_4 = { class: "border-b border-gray-200" }
const _hoisted_5 = { class: "px-3 py-2" }
const _hoisted_6 = { class: "px-3 py-2" }
const _hoisted_7 = { class: "border-b border-gray-200" }
const _hoisted_8 = { class: "px-3 py-2" }
const _hoisted_9 = { class: "border-b border-gray-200" }
const _hoisted_10 = { class: "px-3 py-2" }
const _hoisted_11 = { class: "border-b border-gray-200" }
const _hoisted_12 = { class: "px-3 py-2" }
const _hoisted_13 = { class: "border-b border-gray-200" }
const _hoisted_14 = { class: "px-3 py-2" }
const _hoisted_15 = { class: "px-3 py-2" }
const _hoisted_16 = { class: "border-b border-gray-200" }
const _hoisted_17 = { class: "px-3 py-2" }
const _hoisted_18 = { class: "border-b border-gray-200" }
const _hoisted_19 = { class: "px-3 py-2" }
const _hoisted_20 = {
  key: 2,
  class: "border-b border-gray-200"
}
const _hoisted_21 = { class: "px-3 py-2" }
const _hoisted_22 = {
  key: 3,
  class: "border-b border-gray-200"
}
const _hoisted_23 = { class: "px-3 py-2" }
const _hoisted_24 = { key: 4 }
const _hoisted_25 = {
  colspan: "2",
  class: "px-3 py-3"
}
const _hoisted_26 = { class: "flex flex-col items-start gap-2" }
const _hoisted_27 = ["disabled"]

import { computed, ref, watch } from "vue";
import {
  adminEfpGetCreditNoteAuthUrl,
  adminEfpGetShippingInvoiceUrl,
} from "@/api/adminEfp";
import { useNotification } from "@/composables/useNotification";
import { useAuthStore } from "@/stores/auth";
import type { OrderDetailResponse } from "@/types";
import {
  calculateTVARate,
  isEtranger as checkIsEtranger,
} from "@/utils/tvaUtils";


export default /*@__PURE__*/_defineComponent({
  __name: 'OrderAdminZone',
  props: {
    orderId: {},
    orderDetail: {}
  },
  setup(__props: any) {

const props = __props;

const authStore = useAuthStore();
const { error } = useNotification();
const isAdmin = computed(() => authStore.isImpersonating);

const quantityTotal = computed(() => {
  const lignes = props.orderDetail?.lignes;
  if (!lignes?.length) return 0;
  return lignes
    .filter((l) => l.corbeille === 0)
    .reduce((sum, l) => sum + (l.quantite_total || 0), 0);
});

const poidsTotal = computed(() => props.orderDetail?.poids ?? 0);

const montantTotal = computed(() => props.orderDetail?.montantTotal ?? 0);
const montantCA = computed(() => props.orderDetail?.montantCA ?? 0);
const montantApresRemise = computed(
  () =>
    props.orderDetail?.montantApresRemise ??
    props.orderDetail?.montantTotal ??
    0,
);

const yearBefore2026 = computed(() => {
  const date =
    props.orderDetail?.dateCommande || props.orderDetail?.dateCreation;
  if (!date) return true;
  return new Date(date) < new Date("2026-02-01");
});

const montantApresRemiseDisplay = computed(() =>
  montantCA.value ? montantCA.value : montantApresRemise.value,
);

const hasReduction = computed(() => {
  if (yearBefore2026.value) {
    return montantTotal.value !== montantApresRemise.value;
  }
  const effectiveMontantCA = montantCA.value || montantApresRemise.value;
  return effectiveMontantCA !== montantTotal.value;
});

const reductionAmount = computed(
  () => montantTotal.value - montantApresRemiseDisplay.value,
);

const isEtranger = computed(() => {
  if (!props.orderDetail) return false;
  return checkIsEtranger(props.orderDetail);
});

const tvaRate = computed(() => {
  if (!props.orderDetail) return 0.2;
  return calculateTVARate(
    props.orderDetail.dateCommande || props.orderDetail.dateCreation,
  );
});

const tvaAmount = computed(() => {
  if (isEtranger.value) return 0;
  return montantApresRemiseDisplay.value * tvaRate.value;
});

const totalTTC = computed(() => {
  if (isEtranger.value) return montantApresRemiseDisplay.value;
  return montantApresRemiseDisplay.value * (1 + tvaRate.value);
});

/** Legacy GetFraisPort() → commandeGroupe.frais_port */
const fraisPortHt = computed(() => {
  const fp = props.orderDetail?.commandeGroupe?.frais_port;
  return fp != null ? Number(fp) : 0;
});

const fraisPortSrc = computed(() => {
  const src = props.orderDetail?.commandeGroupe?.frais_port_src;
  return src != null ? Number(src) : 0;
});

/** Legacy $remiseTotalFp = GetRemise(frais_port, "fraisport") */
const remiseFraisPortHt = computed(() => {
  const src = fraisPortSrc.value;
  const fp = fraisPortHt.value;
  if (src > fp) return Math.round((src - fp) * 100) / 100;
  return 0;
});

const fraisPortTtc = computed(() => {
  if (isEtranger.value) return fraisPortHt.value;
  return Math.round(fraisPortHt.value * (1 + tvaRate.value) * 100) / 100;
});

const showFactureFraisPortDownload = computed(() => {
  const d = props.orderDetail;
  if (!d) return false;
  if (d.statut === 5) return false;
  if (d.livraison?.id_livraison === 5) return false;
  return Boolean(
    d.commandeGroupe?.id_commande_groupe_name && d.acheteur?.id_acheteur,
  );
});

const downloadingFactureFraisPort = ref(false);

const openFactureFraisPort = async () => {
  const orderId = props.orderId;
  if (!orderId || downloadingFactureFraisPort.value) return;
  const popup = window.open("about:blank", "_blank");
  downloadingFactureFraisPort.value = true;
  try {
    const url = await adminEfpGetShippingInvoiceUrl(orderId);
    if (url && popup) {
      popup.opener = null;
      popup.location.href = url;
    } else if (url) {
      window.location.href = url;
    }
  } catch (e) {
    popup?.close();
    error("Erreur lors du téléchargement de la facture de frais de port");
    console.error(e);
  } finally {
    downloadingFactureFraisPort.value = false;
  }
};

const avoirUrl = ref("");
const showAvoirDownload = computed(() => Boolean(avoirUrl.value));
let avoirAvailabilityRequest = 0;

const loadAvoirAvailability = async () => {
  const requestId = ++avoirAvailabilityRequest;
  const idAcheteur = props.orderDetail?.acheteur?.id_acheteur;
  const idCommandeGroupe = props.orderDetail?.id_commande_groupe;
  avoirUrl.value = "";
  if (!isAdmin.value || !idAcheteur || !idCommandeGroupe) return;

  try {
    const url = await adminEfpGetCreditNoteAuthUrl(
      idAcheteur,
      idCommandeGroupe,
    );
    if (requestId === avoirAvailabilityRequest) {
      avoirUrl.value = url || "";
    }
  } catch {
    if (requestId === avoirAvailabilityRequest) avoirUrl.value = "";
  }
};

const openAvoir = () => {
  if (!avoirUrl.value) return;
  const popup = window.open(avoirUrl.value, "_blank", "noopener,noreferrer");
  if (!popup) window.location.href = avoirUrl.value;
};

const formatMoney = (value: number): string =>
  `${value.toFixed(2)} €`;

const formatWeight = (value: number): string => value.toFixed(3);

watch(
  () => [
    isAdmin.value,
    props.orderDetail?.acheteur?.id_acheteur,
    props.orderDetail?.id_commande_groupe,
  ] as const,
  loadAvoirAvailability,
  { immediate: true },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[18] || (_cache[18] = _createElementVNode("div", { class: "order-admin-zone__heading px-3 py-2 bg-[#fcf8e3] border-b border-yellow-300" }, [
        _createElementVNode("span", { class: "font-semibold text-[#8a6d3b]" }, "Admin")
      ], -1)),
      _createElementVNode("table", _hoisted_3, [
        _createElementVNode("tbody", null, [
          _createElementVNode("tr", _hoisted_4, [
            _createElementVNode("td", _hoisted_5, [
              _cache[0] || (_cache[0] = _createElementVNode("strong", null, "Quantité totale :", -1)),
              _createTextVNode(" " + _toDisplayString(quantityTotal.value), 1)
            ]),
            _createElementVNode("td", _hoisted_6, [
              _cache[1] || (_cache[1] = _createElementVNode("strong", null, ">Poids total (kg)< :", -1)),
              _createTextVNode(" " + _toDisplayString(formatWeight(poidsTotal.value)) + " kg ", 1)
            ])
          ]),
          _createElementVNode("tr", _hoisted_7, [
            _cache[3] || (_cache[3] = _createElementVNode("td", { class: "px-3 py-2" }, [
              _createElementVNode("strong", null, "Prix total")
            ], -1)),
            _createElementVNode("td", _hoisted_8, [
              _createTextVNode(_toDisplayString(formatMoney(montantTotal.value)) + " ", 1),
              _cache[2] || (_cache[2] = _createElementVNode("span", { class: "text-gray-600" }, "HT", -1))
            ])
          ]),
          (hasReduction.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createElementVNode("tr", _hoisted_9, [
                  _cache[5] || (_cache[5] = _createElementVNode("td", { class: "px-3 py-2" }, [
                    _createElementVNode("strong", null, [
                      _createTextVNode("Remise "),
                      _createElementVNode("span", { class: "text-gray-600" }, "HT"),
                      _createTextVNode(" :")
                    ])
                  ], -1)),
                  _createElementVNode("td", _hoisted_10, [
                    _createTextVNode(_toDisplayString(formatMoney(reductionAmount.value)) + " ", 1),
                    _cache[4] || (_cache[4] = _createElementVNode("span", { class: "text-gray-600" }, "HT", -1))
                  ])
                ]),
                _createElementVNode("tr", _hoisted_11, [
                  _cache[7] || (_cache[7] = _createElementVNode("td", { class: "px-3 py-2" }, [
                    _createElementVNode("strong", null, "Prix HT après remise :")
                  ], -1)),
                  _createElementVNode("td", _hoisted_12, [
                    _createTextVNode(_toDisplayString(formatMoney(montantApresRemiseDisplay.value)) + " ", 1),
                    _cache[6] || (_cache[6] = _createElementVNode("span", { class: "text-gray-600" }, "HT", -1))
                  ])
                ])
              ], 64))
            : _createCommentVNode("", true),
          (!isEtranger.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _createElementVNode("tr", _hoisted_13, [
                  _createElementVNode("td", _hoisted_14, [
                    _createElementVNode("strong", null, "TVA (" + _toDisplayString(Math.round(tvaRate.value * 100)) + "%) :", 1)
                  ]),
                  _createElementVNode("td", _hoisted_15, _toDisplayString(formatMoney(tvaAmount.value)), 1)
                ]),
                _createElementVNode("tr", _hoisted_16, [
                  _cache[9] || (_cache[9] = _createElementVNode("td", { class: "px-3 py-2" }, [
                    _createElementVNode("strong", null, "Prix total")
                  ], -1)),
                  _createElementVNode("td", _hoisted_17, [
                    _createTextVNode(_toDisplayString(formatMoney(totalTTC.value)) + " ", 1),
                    _cache[8] || (_cache[8] = _createElementVNode("span", { class: "text-gray-600" }, "TTC", -1))
                  ])
                ])
              ], 64))
            : _createCommentVNode("", true),
          _createElementVNode("tr", _hoisted_18, [
            _cache[11] || (_cache[11] = _createElementVNode("td", { class: "px-3 py-2" }, [
              _createElementVNode("strong", null, "Frais de port")
            ], -1)),
            _createElementVNode("td", _hoisted_19, [
              _createTextVNode(_toDisplayString(formatMoney(fraisPortHt.value)) + " ", 1),
              _cache[10] || (_cache[10] = _createElementVNode("span", { class: "text-gray-600" }, "HT", -1))
            ])
          ]),
          (remiseFraisPortHt.value > 0)
            ? (_openBlock(), _createElementBlock("tr", _hoisted_20, [
                _cache[13] || (_cache[13] = _createElementVNode("td", { class: "px-3 py-2" }, [
                  _createElementVNode("strong", null, [
                    _createTextVNode("Remise Frais de port "),
                    _createElementVNode("span", { class: "text-gray-600" }, "HT"),
                    _createTextVNode(" :")
                  ])
                ], -1)),
                _createElementVNode("td", _hoisted_21, [
                  _createTextVNode(_toDisplayString(formatMoney(remiseFraisPortHt.value)) + " ", 1),
                  _cache[12] || (_cache[12] = _createElementVNode("span", { class: "text-gray-600" }, "HT", -1))
                ])
              ]))
            : _createCommentVNode("", true),
          (!isEtranger.value)
            ? (_openBlock(), _createElementBlock("tr", _hoisted_22, [
                _cache[15] || (_cache[15] = _createElementVNode("td", { class: "px-3 py-2" }, [
                  _createElementVNode("strong", null, "Frais de port")
                ], -1)),
                _createElementVNode("td", _hoisted_23, [
                  _createTextVNode(_toDisplayString(formatMoney(fraisPortTtc.value)) + " ", 1),
                  _cache[14] || (_cache[14] = _createElementVNode("span", { class: "text-gray-600" }, "TTC", -1))
                ])
              ]))
            : _createCommentVNode("", true),
          (showFactureFraisPortDownload.value)
            ? (_openBlock(), _createElementBlock("tr", _hoisted_24, [
                _createElementVNode("td", _hoisted_25, [
                  _createElementVNode("div", _hoisted_26, [
                    _createElementVNode("button", {
                      type: "button",
                      disabled: downloadingFactureFraisPort.value,
                      class: "inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50",
                      onClick: openFactureFraisPort
                    }, _cache[16] || (_cache[16] = [
                      _createElementVNode("i", { class: "fas fa-download text-gray-600" }, null, -1),
                      _createTextVNode(" Télécharger la facture de frais de port ")
                    ]), 8, _hoisted_27),
                    (showAvoirDownload.value)
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          type: "button",
                          class: "inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50",
                          onClick: openAvoir
                        }, _cache[17] || (_cache[17] = [
                          _createElementVNode("i", { class: "fas fa-download text-gray-600" }, null, -1),
                          _createTextVNode(" Télécharger l'avoir ")
                        ])))
                      : _createCommentVNode("", true)
                  ])
                ])
              ]))
            : _createCommentVNode("", true)
        ])
      ])
    ])
  ]))
}
}

})